/**
 * Elite Fade Designs - Public Chat Service
 *
 * Wraps /v1/chat/* endpoints and stores the chat session in localStorage so
 * a visitor can resume their conversation across page loads.
 *
 * localStorage key: efd_chat_session = {
 *   conversationId, sessionToken, customerName, customerEmail, customerPhone
 * }
 */

import { config } from '../config/index.js';

const STORAGE_KEY = 'efd_chat_session';
const BASE = `${config.apiUrl}/v1/chat`;

// ---------------------------------------------------------------------------
// Session persistence
// ---------------------------------------------------------------------------
export function getSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveSession(session) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (_) { /* ignore quota */ }
}

export function clearSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* */ }
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------
async function chatFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const session = getSession();
  if (session?.sessionToken && !headers['X-Chat-Token']) {
    headers['X-Chat-Token'] = session.sessionToken;
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  let body;
  try { body = await res.json(); }
  catch { body = null; }

  if (res.status === 401) {
    clearSession();
    const err = new Error('Chat session expired');
    err.status = 401;
    throw err;
  }

  if (!res.ok || (body && body.success === false)) {
    const msg = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }

  return body?.data ?? body;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start a new chat (or attach to an existing open one by email).
 * Persists the session token + customer info to localStorage.
 */
export async function startChat({ name, email, phone, message }) {
  const data = await chatFetch('/start', {
    method: 'POST',
    body: JSON.stringify({ name, email, phone, message }),
  });
  const session = {
    conversationId: data.conversation.id,
    sessionToken: data.sessionToken,
    customerName: name,
    customerEmail: email || null,
    customerPhone: phone || null,
  };
  saveSession(session);
  return data.conversation;
}

export function getConversation() {
  const session = getSession();
  if (!session) throw new Error('No active chat session');
  return chatFetch(`/conversations/${encodeURIComponent(session.conversationId)}`)
    .then((d) => d.conversation);
}

export function getMessages(since) {
  const session = getSession();
  if (!session) throw new Error('No active chat session');
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  return chatFetch(`/conversations/${encodeURIComponent(session.conversationId)}/messages${qs}`)
    .then((d) => d.messages);
}

export function sendMessage(body) {
  const session = getSession();
  if (!session) throw new Error('No active chat session');
  return chatFetch(`/conversations/${encodeURIComponent(session.conversationId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  }).then((d) => d.message);
}

export function markRead() {
  const session = getSession();
  if (!session) return Promise.resolve(null);
  return chatFetch(`/conversations/${encodeURIComponent(session.conversationId)}/read`, {
    method: 'POST',
    body: '{}',
  }).catch(() => null);  // never block UI on read receipts
}
